import React from 'react';

import { Button } from '../ui/button';

import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';

import { Settings, RefreshCw, Euro, Calendar, Percent, Loader2, ArrowRightLeft, Archive, History, BarChart3, Users, Building, Briefcase, Shield, Layers, Tag, MapPin, Hash } from 'lucide-react';

const _API = process.env.REACT_APP_BACKEND_URL;

const RCDS_TYPES = [
  { id: 'annuel', label: 'RCDS Annuel', icon: Calendar, color: 'emerald' },
  { id: 'nominatif', label: 'RCDS Nominatif', icon: Briefcase, color: 'blue' }
];

const _ESPACES = [
  { id: 'super_admin', label: 'Super Admin', icon: Shield },
  { id: 'admin', label: 'Admin', icon: Settings },
  { id: 'cooper', label: "Cooper's", icon: Building },
  { id: 'member', label: 'Membre', icon: Users },
  { id: 'expert', label: 'Expert', icon: Briefcase }
];

import { useBaticoConfig } from './useBaticoConfig';
import BaticoTabsA from './BaticoTabsA';
import BaticoTabsB from './BaticoTabsB';
import BaticoTabsC from './BaticoTabsC';

export default function BaticoConfigV3({ getAuthHeader }) {
  const ctx = useBaticoConfig(getAuthHeader);
  const { activeRcds, activeTab, activites, archives: archives, coefficients: coefficients, config, displayTaxes, history: history, loadData, loading, nomenclatures: nomenclatures, remises: remises, setActiveRcds, setActiveTab, stats: stats, taxes: taxes } = ctx;
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-10 w-10 animate-spin text-amber-500" />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="h-7 w-7" />
              Configuration BATICO V3
            </h1>
            <p className="text-amber-100 mt-1">Gestion complète RCDS Annuel / Nominatif</p>
          </div>
          <Button onClick={loadData} variant="secondary" className="bg-white/20 hover:bg-white/30 text-white">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>
        
        <div className="grid grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold">{config?.stats?.total_syncs || 0}</div>
            <div className="text-xs text-amber-100">Syncs</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold">{config?.stats?.total_modifications || 0}</div>
            <div className="text-xs text-amber-100">Modifs</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold">{displayTaxes.length}</div>
            <div className="text-xs text-amber-100">Taxes actives</div>
          </div>
          <div className="bg-white/10 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold">{activites.length}</div>
            <div className="text-xs text-amber-100">Activités</div>
          </div>
        </div>
      </div>

      {/* RCDS Selector */}
      <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
        {RCDS_TYPES.map(type => (
          <button
            key={type.id}
            onClick={() => setActiveRcds(type.id)}
            className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              activeRcds === type.id
                ? 'bg-white shadow-lg text-slate-800'
                : 'text-slate-500 hover:bg-white/50'
            }`}
          >
            <type.icon className="h-5 w-5" />
            {type.label}
          </button>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-8 gap-1 bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="seuils" className="text-xs"><Euro className="h-4 w-4 mr-1" />Seuils</TabsTrigger>
          <TabsTrigger value="taxes" className="text-xs"><Percent className="h-4 w-4 mr-1" />Contributions</TabsTrigger>
          <TabsTrigger value="remises" className="text-xs"><Tag className="h-4 w-4 mr-1" />Remises</TabsTrigger>
          <TabsTrigger value="coefficients" className="text-xs"><Hash className="h-4 w-4 mr-1" />Coefs</TabsTrigger>
          <TabsTrigger value="operations" className="text-xs" data-testid="batico-v3-tab-operations">
            <MapPin className="h-4 w-4 mr-1" />Opérations
            <span className="ml-1 text-[10px] text-slate-400 hidden lg:inline">(Nominative)</span>
          </TabsTrigger>
          <TabsTrigger value="nomenclatures" className="text-xs"><Layers className="h-4 w-4 mr-1" />Nomencl.</TabsTrigger>
          <TabsTrigger value="sync" className="text-xs"><ArrowRightLeft className="h-4 w-4 mr-1" />Sync</TabsTrigger>
          <TabsTrigger value="archives" className="text-xs"><Archive className="h-4 w-4 mr-1" />Archives</TabsTrigger>
          <TabsTrigger value="stats" className="text-xs"><BarChart3 className="h-4 w-4 mr-1" />Stats</TabsTrigger>
          <TabsTrigger value="history" className="text-xs"><History className="h-4 w-4 mr-1" />Historique</TabsTrigger>
          <TabsTrigger value="eligibility" className="text-xs" data-testid="tab-eligibility"><Shield className="h-4 w-4 mr-1" />Modulateurs éligibilité</TabsTrigger>
        </TabsList>

        <BaticoTabsA ctx={ctx} />
        <BaticoTabsB ctx={ctx} />
        <BaticoTabsC ctx={ctx} />
      </Tabs>
    </div>
  );
}
